/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 10 2025
 **/

import { useAtomValue } from "jotai";
import { atomWithReducer } from "jotai/utils";
import { Origin, SidebarManagerState, SidebarState } from "./types";

const defaultSidebarState: SidebarManagerState = {
	id: undefined,
	origin: {},
	order: {},
	action: null,
	focusedId: undefined
};

export function reducer(state: SidebarManagerState = defaultSidebarState, action: PayloadAction<SidebarState>) {
	switch (action.type) {
		case "reset": {
			return defaultSidebarState;
		}
		case "resetServiceId": {
			const { serviceId } = action.payload;
			const newState = {
				...state,
				order: {
					...state.order,
					[serviceId!]: { left: [], right: [] }
				}
			};
			return newState;
		}
		case "push": {
			const item = state.order[action.payload.serviceId!] || { left: [], right: [] };
			const _left = action.payload.option?.direction === "left" ? [...item.left, action.payload.id!] : item.left;
			const _right =
				action.payload.option?.direction === "right" ? [...item.right, action.payload.id!] : item.right;
			return {
				...state,
				id: action.payload.id,
				origin: { ...state.origin, [action.payload.id!]: action.payload },
				order: { ...state.order, [action.payload.serviceId!]: { left: _left, right: _right } },
				action: action.type
			};
		}
		case "show":
			return {
				...state,
				id: action.payload.id,
				origin: {
					...state.origin,
					[action.payload.id!]: { ...state.origin[action.payload.id!], isOpen: true }
				},
				action: action.type
			};
		case "hide":
			return {
				...state,
				id: action.payload.id,
				origin: {
					...state.origin,
					[action.payload.id!]: { ...state.origin[action.payload.id!], isOpen: false }
				},
				action: action.type
			};
		case "updateOption":
			return {
				...state,
				id: action.payload.id,
				origin: {
					...state.origin,
					[action.payload.id!]: {
						...state.origin[action.payload.id!],
						option: {
							...state.origin[action.payload.id!].option,
							...action.payload.option
						}
					}
				} as Origin,
				action: action.type
			};
		case "updateAnimationStatus":
			return {
				...state,
				id: action.payload.id,
				origin: {
					...state.origin,
					[action.payload.id!]: {
						...state.origin[action.payload.id!],
						_animationStatus: action.payload._animationStatus
					}
				},
				action: action.type
			};

		case "addOnBeforeHideEvent":
			return {
				...state,
				id: action.payload.id,
				origin: {
					...state.origin,
					[action.payload.id!]: {
						...state.origin[action.payload.id!],
						onBeforeHide: action.payload.onBeforeHide
					}
				},
				action: action.type
			};

		case "setFocus":
			return {
				...state,
				focusedId: action.payload.id
			};

		default:
			return state;
	}
}

export const sidebarReducerAtom = atomWithReducer(defaultSidebarState, reducer);

export default function useSidebarState(): SidebarManagerState {
	return useAtomValue(sidebarReducerAtom);
}
